import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { provedorOpencodeNaoTratado, redigirChaveOpencode, type ProvedorOpencode } from '@remoto/protocolo';
import {
  nomeDoProvedorOpencode,
  ROTAS,
  TesteDeChaveSchema,
  TrocarChaveOpencodeSchema,
  type ChaveOpencodeVisivel,
  type TesteDeChave,
} from '@remoto/shared';
import { chamar } from '../api';
import { Cartao } from '../componentes/layout';
import { Campo } from '../componentes/campos';
import { usarConfirmacao } from '../componentes/Modal';
import { IconeAviso } from '../componentes/icones';
import { mensagemDoErro } from '../erros';
import { tempoRelativo } from '../formato';
import { CHAVE_DA_CONFIG, conferir, FRASES_DA_TELA } from './dados';
import { dinheiro, quando } from './regras';

const BOTAO =
  'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-40 pointer-coarse:min-h-11';

/** A cara da chave de cada provedor, para o `placeholder` — é a mesma que a redação reconhece. */
function caraDaChave(provedor: ProvedorOpencode): string {
  switch (provedor) {
    case 'nanogpt':
      return 'sk-nano-…';
    case 'openrouter':
      return 'sk-or-…';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** A linha de baixo da confirmação de trocar a chave ATIVA: o que limita o gasto em cada provedor. */
function lembreteAoTrocar(provedor: ProvedorOpencode): string {
  switch (provedor) {
    case 'nanogpt':
      return 'A assinatura vem primeiro; acabada a cota da semana, o NanoGPT cobra do crédito da conta.';
    case 'openrouter':
      return 'Confira antes se a chave tem teto de gasto no painel do OpenRouter.';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O teste que deu certo, como cada provedor responde. */
function TesteAceito({ provedor, teste }: { provedor: ProvedorOpencode; teste: TesteDeChave }): React.JSX.Element {
  switch (provedor) {
    case 'nanogpt':
      /*
        O NanoGPT não tem teto POR CHAVE (F2): o que o teste lê é o saldo da
        conta (`check-balance`). A assinatura é da conta e mora no cartão do
        OpenCode, na tela de Uso — repeti-la aqui seria uma segunda leitura que
        envelhece separada.
      */
      return (
        <dl className="grid grid-cols-1 gap-2">
          <div className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-zinc-500">Saldo de crédito</dt>
            <dd data-testid="saldo-do-teste" className="truncate tabular-nums text-zinc-100">
              {teste.restanteUsd === null ? 'não informado' : dinheiro(teste.restanteUsd)}
            </dd>
          </div>
        </dl>
      );
    case 'openrouter': {
      const linhas: [string, number | null][] = [
        ['Teto', teste.limiteUsd],
        ['Usado', teste.usoUsd],
        ['Restante', teste.restanteUsd],
      ];
      return (
        <>
          <dl className="grid grid-cols-3 gap-2">
            {linhas.map(([nome, valor]) => (
              <div key={nome} className="min-w-0">
                <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{nome}</dt>
                <dd className="truncate tabular-nums text-zinc-100">
                  {valor === null ? (nome === 'Teto' ? 'sem teto' : '—') : dinheiro(valor)}
                </dd>
              </div>
            ))}
          </dl>
          {teste.limiteUsd === null ? (
            /*
              D8: no OpenRouter o teto na chave é a única trava de gasto do
              OpenCode — não há orçamento diário nem aviso de crédito (D14).
              Chave sem teto é gasto sem limite, e isso tem de aparecer no
              momento em que ela é testada.
            */
            <p className="flex items-start gap-1.5 text-xs text-amber-200">
              <IconeAviso tamanho={14} className="mt-0.5" />
              Esta chave não tem teto de gasto. Ponha um no painel do OpenRouter antes de usá-la na
              frota.
            </p>
          ) : null}
        </>
      );
    }
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O resultado do "Testar": o que o provedor disse da chave, ou o motivo da recusa. */
function ResultadoDoTeste({ provedor, teste }: { provedor: ProvedorOpencode; teste: TesteDeChave }): React.JSX.Element {
  const nome = nomeDoProvedorOpencode(provedor);
  if (!teste.ok) {
    return (
      <div
        data-testid="resultado-do-teste"
        role="status"
        className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200"
      >
        <p>O {nome} recusou esta chave.</p>
        {/*
          O detalhe é o que o provedor respondeu, e passa pela redação de novo
          aqui: o servidor já redige, e esta é a segunda barreira, que não
          depende da primeira ter funcionado.
        */}
        {teste.erro ? (
          <p className="mt-1 text-xs text-red-300/80">{redigirChaveOpencode(teste.erro)}</p>
        ) : null}
      </div>
    );
  }
  return (
    <div
      data-testid="resultado-do-teste"
      role="status"
      className="space-y-2 rounded-md border border-emerald-900/60 bg-emerald-950/20 px-3 py-2 text-sm"
    >
      <p className="text-emerald-200">O {nome} aceitou a chave.</p>
      <TesteAceito provedor={provedor} teste={teste} />
    </div>
  );
}

/** Os avisos que acompanham a chave de cada provedor: o que limita o gasto é diferente nos dois. */
function AvisoDoGasto({ provedor }: { provedor: ProvedorOpencode }): React.JSX.Element {
  switch (provedor) {
    case 'nanogpt':
      return (
        <li>
          A assinatura vem primeiro: quando a cota da semana acaba, as chamadas passam a gastar o
          crédito da conta do NanoGPT. A assinatura, o saldo e o gasto em crédito aparecem no
          cartão do OpenCode, na tela de Uso.
        </li>
      );
    case 'openrouter':
      return (
        <li>
          Ponha um teto de gasto na chave no painel do OpenRouter. É ele que limita o que a frota
          pode gastar.
        </li>
      );
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * A chave de UM provedor de modelos da frota (D5, D6, D8, D17; F2: NanoGPT e
 * OpenRouter, cada um com a sua).
 *
 * Um cartão por provedor, e não um campo com um seletor ao lado: o dono vê as
 * DUAS de uma vez, cada uma com o seu final (`…ab12`), e reconhece qual é
 * qual sem abrir nada. A do provedor ATIVO é a que a frota usa; a do outro fica
 * GUARDADA — trocá-la ou removê-la não mexe em máquina nenhuma —, e é ela que
 * vale no dia em que o provedor for trocado. Por isso todo pedido diz o
 * `provedor` por extenso: depender do "ausente = o ativo" faria uma tela aberta
 * antes de alguém trocar o provedor mexer na chave errada.
 *
 * **O campo é só de escrita.** O servidor nunca devolve a chave — a tela
 * conhece o final dela, a versão e quando trocou —, e o que a pessoa digita
 * vive só no estado desta seção, até salvar. Depois de salvar, o campo volta
 * VAZIO: é a tela afirmando que não guarda o que acabou de enviar.
 *
 * As chamadas são `chamar` direto, e não `useMutation`: a mutação do React
 * Query guarda as `variables` no cache dela até o coletor passar, e a
 * variável aqui é o segredo.
 */
export function SecaoDaChave({
  provedor,
  chave,
  provedorAtivo,
}: {
  provedor: ProvedorOpencode;
  chave: ChaveOpencodeVisivel | null;
  /** O provedor que a frota usa agora. É ele que decide se esta chave mexe nas máquinas. */
  provedorAtivo: ProvedorOpencode;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const [pedir, dialogo] = usarConfirmacao();
  const [segredo, setSegredo] = useState('');
  const [teste, setTeste] = useState<TesteDeChave | null>(null);
  const [ocupado, setOcupado] = useState<'testar' | 'salvar' | 'remover' | null>(null);
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState('');

  const nome = nomeDoProvedorOpencode(provedor);
  const nomeDoAtivo = nomeDoProvedorOpencode(provedorAtivo);
  const ativo = provedor === provedorAtivo;

  const candidata = TrocarChaveOpencodeSchema.safeParse({ segredo, provedor });
  const digitou = segredo.trim() !== '';
  // Vazio testa a chave GUARDADA — só existe se houver uma.
  const podeTestar = ocupado === null && (digitou ? candidata.success : chave !== null);
  const podeSalvar = ocupado === null && candidata.success;

  function digitar(texto: string): void {
    setSegredo(texto);
    // O resultado do teste era da chave de antes da tecla: mostrá-lo ao lado
    // da chave nova diria que ela foi testada.
    setTeste(null);
    setErro('');
    setFeito('');
  }

  async function testar(): Promise<void> {
    setOcupado('testar');
    setErro('');
    setTeste(null);
    try {
      const corpo = digitou && candidata.success ? { segredo: candidata.data.segredo, provedor } : { provedor };
      const resposta = await chamar<unknown>(ROTAS.configuracoes.testarChaveOpencode, {
        method: 'POST',
        corpo,
      });
      setTeste(conferir(TesteDeChaveSchema, resposta));
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não foi possível testar a chave agora.', FRASES_DA_TELA));
    } finally {
      setOcupado(null);
    }
  }

  async function gravar(valor: string): Promise<void> {
    setOcupado('salvar');
    setErro('');
    try {
      await chamar(ROTAS.configuracoes.chaveOpencode, { method: 'PUT', corpo: { segredo: valor, provedor } });
      setSegredo('');
      setTeste(null);
      setFeito(
        ativo
          ? 'Chave trocada. Cada máquina reinicia o OpenCode com ela assim que não houver turno em andamento.'
          : `Chave guardada. A frota continua no ${nomeDoAtivo}; esta chave vale quando o provedor for trocado para o ${nome}.`,
      );
      await cliente.invalidateQueries({ queryKey: CHAVE_DA_CONFIG });
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não foi possível salvar a chave.', FRASES_DA_TELA));
    } finally {
      setOcupado(null);
    }
  }

  async function apagar(): Promise<void> {
    setOcupado('remover');
    setErro('');
    try {
      await chamar(`${ROTAS.configuracoes.chaveOpencode}?provedor=${provedor}`, { method: 'DELETE' });
      setTeste(null);
      setFeito(
        ativo
          ? 'Chave removida. As máquinas derrubam os processos do OpenCode.'
          : `Chave removida. A frota continua no ${nomeDoAtivo}, sem mudança.`,
      );
      await cliente.invalidateQueries({ queryKey: CHAVE_DA_CONFIG });
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não foi possível remover a chave.', FRASES_DA_TELA));
    } finally {
      setOcupado(null);
    }
  }

  function salvar(): void {
    if (!candidata.success) return;
    // O valor é capturado AGORA: o que vai é o que estava no campo quando a
    // pessoa pediu, não o que estiver lá quando ela confirmar.
    const valor = candidata.data.segredo;
    pedir({
      titulo: `${chave ? 'Trocar' : 'Cadastrar'} a chave do ${nome}`,
      texto: ativo ? (
        <div className="space-y-2">
          <p>
            Todas as máquinas com OpenCode passam a usar esta chave. Quem tiver turno em andamento
            termina o turno antes de trocar — e, passados 10 minutos, o turno é interrompido.
          </p>
          <p className="text-zinc-400">{lembreteAoTrocar(provedor)}</p>
        </div>
      ) : (
        <p>
          A chave fica guardada: a frota continua no {nomeDoAtivo}, e nenhuma máquina reinicia. Ela
          passa a valer quando o provedor for trocado para o {nome}, no cartão de modelos.
        </p>
      ),
      rotuloConfirmar: chave ? 'Trocar a chave' : 'Cadastrar a chave',
      aoConfirmar: () => void gravar(valor),
    });
  }

  function remover(): void {
    pedir({
      titulo: `Remover a chave do ${nome}`,
      texto: ativo
        ? 'Sem chave, nenhuma sessão OpenCode nova nasce e as máquinas derrubam os processos do OpenCode — inclusive os que estão trabalhando. As conversas continuam guardadas.'
        : `A frota continua no ${nomeDoAtivo}, sem mudança. Sem esta chave, o provedor só pode ser trocado para o ${nome} depois de cadastrar outra.`,
      rotuloConfirmar: 'Remover a chave',
      perigosa: true,
      aoConfirmar: () => void apagar(),
    });
  }

  return (
    <Cartao
      titulo={`Chave do ${nome}`}
      acao={
        <span
          data-testid="papel-da-chave"
          className={
            'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' +
            (ativo ? 'bg-emerald-500/10 text-emerald-300' : 'bg-zinc-800 text-zinc-400')
          }
        >
          {ativo ? 'ativo' : 'guardado'}
        </span>
      }
    >
      <div data-testid="cartao-da-chave" data-provedor={provedor} className="space-y-4">
        <p data-testid="estado-da-chave" className="text-sm text-zinc-300">
          {chave ? (
            <>
              Configurada, termina em <span className="font-mono text-zinc-100">…{chave.final}</span>{' '}
              · versão {chave.versao} · trocada {quando(tempoRelativo(chave.trocadaEm))}
            </>
          ) : ativo ? (
            <>Não configurada. Sem chave, nenhuma sessão OpenCode nasce.</>
          ) : (
            <>Não configurada. Sem ela, o provedor não pode ser trocado para o {nome}.</>
          )}
        </p>
        {ativo ? null : (
          <p className="text-xs text-zinc-500">
            Guardado: a frota usa o {nomeDoAtivo}. Mexer nesta chave não reinicia máquina nenhuma.
          </p>
        )}

        <Campo
          segredo
          rotulo={chave ? `Chave nova do ${nome}` : `Chave do ${nome}`}
          name={`chave-${provedor}-da-frota`}
          value={segredo}
          onChange={(e) => digitar(e.target.value)}
          placeholder={caraDaChave(provedor)}
          dica={
            // Os botões desligam com a chave fora da medida; sem esta linha,
            // desligariam calados.
            digitou && !candidata.success
              ? `Isto não tem a medida de uma chave do ${nome} (de 20 a 300 caracteres).`
              : 'Só de escrita: depois de salvar, a tela mostra apenas o final.'
          }
        />

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void testar()}
            disabled={!podeTestar}
            className={BOTAO + ' border border-zinc-700 text-zinc-200 hover:bg-zinc-800'}
          >
            {ocupado === 'testar'
              ? 'Testando…'
              : digitou || !chave
                ? 'Testar esta chave'
                : 'Testar a chave guardada'}
          </button>
          <button
            type="button"
            onClick={salvar}
            disabled={!podeSalvar}
            className={BOTAO + ' bg-sky-600 font-medium text-white hover:bg-sky-500'}
          >
            {ocupado === 'salvar' ? 'Salvando…' : 'Salvar chave'}
          </button>
          {chave ? (
            <button
              type="button"
              onClick={remover}
              disabled={ocupado !== null}
              className={BOTAO + ' border border-red-900/70 text-red-300 hover:bg-red-950/40'}
            >
              {ocupado === 'remover' ? 'Removendo…' : 'Remover chave'}
            </button>
          ) : null}
        </div>

        {teste ? <ResultadoDoTeste provedor={provedor} teste={teste} /> : null}
        {erro ? (
          <p role="alert" className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200">
            {erro}
          </p>
        ) : null}
        {feito ? (
          <p role="status" className="text-sm text-emerald-300">
            {feito}
          </p>
        ) : null}

        <ul data-testid="avisos-da-chave" className="space-y-1.5 border-t border-zinc-800 pt-3 text-xs text-zinc-400">
          <li>A chave vale para a frota inteira: todas as máquinas com OpenCode usam a mesma.</li>
          <AvisoDoGasto provedor={provedor} />
          {ativo ? (
            <li>
              O cérebro usa outra chave, guardada no <span className="font-mono">.env</span> dele —
              esta tela não mexe nela.
            </li>
          ) : null}
        </ul>
      </div>
      {dialogo}
    </Cartao>
  );
}
